import { useState, useEffect, useCallback, useMemo } from 'react'
import { Table, Empty } from 'antd'
import ReactDragColumnView from '../../../Components/Drag/ReactDragColumnView'
import {
  removeColumn,
  setFullEditSelection,
  toggleAvailabilityFilter,
  setActiveDropdown,
} from '../../../Slices/Kiosk/TableOptionsSlice'
import { saveRowKeys } from '../../../Slices/Kiosk/ArticleSlice'
import { fetchAllArticleDetails, populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import { formatString } from '../../../Helpers/StringHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  renderTranslation,
  renderSelectionCountHtml,
  retrieveColumnWidth,
  sendMessage,
} from '../../../Helpers/GenericHelpers'
import {
  AllFreeWidthColumn,
  PriceFilters,
  PriceListsFilters,
  MovementsOption,
  AvailabilitiesOption,
  ExistanceOption,
  ConfigurationForm,
} from '../../../Constants/Configuration'
import {
  ArticleColumn,
  defaultSingleRowHeight,
  defaultArticlesToShow,
  articleRowHeight,
  ArticleDefaultMarketplaceColLength,
  emptyTableHeight,
  SizeSpecialColumn,
  SizeWithAvailabilitySpecialColumn,
  ArticleDefaultColumn,
} from '../../../Constants/TableConfiguration'
import { getSearchedTableFiltersValue, retrieveActiveEmptyColumns } from '../../../Helpers/FilterHelper'
import ManigliaIcon from '../../../Components/Svg/ManigliaIcon'
import {
  generateTableColumnsToRender,
  handleAvailabiltyAndSizeColumnPosition,
  mapArticleImagesAndDescription,
  scrollElementHeightPositionHandler,
  scrollElementHeightPositionTouchHandler,
  scrollToTableRow,
  triggerPagination,
} from '../../../Helpers/TableHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { TableRowSelection } from 'antd/lib/table/interface'

function Articles() {
  const dispatch = useAppDispatch()
  const sorting = useAppSelector(state => state.present.tableOptions.sorting)
  const loading = useAppSelector(state => state.present.article.loading)
  const article = useAppSelector(state => state.present.article.value)
  const count = useAppSelector(state => state.present.tableOptions.count)
  const tableColumns = useAppSelector(state => state.present.tableOptions.tableColumns)
  const pricelists = useAppSelector(state => state.present.tableOptions.pricelists)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const language = useAppSelector(state => state.present.language.value)
  const emptyTableColumns = useAppSelector(state => state.present.tableOptions.emptyTableColumns)
  const negateTableColumns = useAppSelector(state => state.present.tableOptions.negateTableColumns)
  const freeWidthColumns = useAppSelector(state => state.present.tableOptions.freeWidthColumns)
  const allFreeWidthColumns = freeWidthColumns.indexOf(AllFreeWidthColumn) !== -1
  const alignLeftColumns = useAppSelector(state => state.present.tableOptions.alignLeftColumns)
  const alignRightColumns = useAppSelector(state => state.present.tableOptions.alignRightColumns)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const extraDescriptionLang = useAppSelector(state => state.present.tableOptions.extraDescriptionLang)
  const fullEditSelection = useAppSelector(state => state.present.tableOptions.fullEditSelection)
  const newLineColumns = useAppSelector(state => state.present.tableOptions.newLineColumns)
  const priceList1 = useAppSelector(state => state.present.tableOptions.priceList1)
  const priceList2 = useAppSelector(state => state.present.tableOptions.priceList2)
  const activeDropdown = useAppSelector(state => state.present.tableOptions.activeDropdown)
  const filterSearchValue = useAppSelector(state => state.present.tableOptions.filterSearchCurrentValue)
  const emptyExtraDescriptions = useAppSelector(state => state.present.tableOptions.emptyExtraDescriptions)
  const tableFilters = useAppSelector(state => state.present.tableOptions.tableFilters)
  const priceFilter = useAppSelector(state => state.present.tableOptions.priceFilter)
  const defaultCols = useAppSelector(state => state.present.tableOptions.defaultColumns)
  const formConfigurationValue = useAppSelector(state => state.present.configuration.formConfigurationValue)
  const allMarketplace = useAppSelector(state => state.present.configuration.allMarketplace)
  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)
  const isPurchasePriceVisibile = useAppSelector(state => state.present.configuration.isPurchasePriceVisibile)
  const filterByAvailability = useAppSelector(state => state.present.tableOptions.filterByAvailability)

  //state changes
  const [columns, setColumns] = useState([])
  //Resizable table height
  const [ySize, setYSize] = useState(emptyTableHeight)

  const articles = useMemo(() => {
    return article ? article.results : []
  }, [article])

  useEffect(() => {
    setTimeout(() => {
      const tableWrapper = document.querySelector('#article-table .ant-table-body')
      const addedHeight = tableWrapper && tableWrapper.scrollWidth !== tableWrapper.clientWidth ? 25 : 0

      const desiredHeight =
        Math.max(
          articles.length === 0
            ? emptyTableHeight
            : Math.min(articles.length, defaultArticlesToShow) * articleRowHeight,
          articles.length === 0 ? emptyTableHeight : defaultSingleRowHeight,
        ) + addedHeight

      if (desiredHeight !== ySize + 100) {
        setYSize(desiredHeight)
      }
    }, 200)
  }, [articles.length])

  const toggleAvailability = useCallback(() => {
    dispatch(toggleAvailabilityFilter())
    dispatch(populateTableWithArticles())
  }, [dispatch])

  const isConfigurationFormGruccia = formConfigurationValue === ConfigurationForm.GRUCCIA

  const handleTableChange = useCallback(
    (pagination, sorting) => {
      dispatch(triggerPagination(sorting, pagination, true, editTableDisable))
    },
    [dispatch, editTableDisable],
  )

  useEffect(() => {
    const visibleColumns: ArticleDefaultColumn[] = []
    for (const group in tableColumns) {
      if (group === PriceListsFilters || group === PriceFilters) {
        continue
      }
      const columnsToShow = tableColumns[group].filter(x => x.visible)

      for (const filter in columnsToShow) {
        if (!columnsToShow[filter].active) {
          continue
        }
        if (
          columnsToShow[filter].value === ArticleColumn.ShortDescription ||
          columnsToShow[filter].value === ArticleColumn.SpecialDescription ||
          columnsToShow[filter].value === ArticleColumn.Description ||
          columnsToShow[filter].value === ArticleColumn.SizeAndFit
        ) {
          Object.keys(extraDescriptionLang).forEach(lang => {
            if (extraDescriptionLang[lang].indexOf(columnsToShow[filter].value) !== -1) {
              const activeColumns = defaultCols.find(
                item => item.name === columnsToShow[filter].value && item.language === lang,
              )
              if (activeColumns) {
                visibleColumns.push(activeColumns)
              }
            }
          })
        } else {
          const activeColumns = defaultCols.find(item => item.name === columnsToShow[filter].value)
          if (activeColumns) {
            visibleColumns.push(activeColumns)
          }
        }
      }
    }

    for (const filter in tableColumns.priceFilters) {
      if (pricelists.priceList1Enabled) {
        const activeColumns = defaultCols.find(
          (item, index) =>
            item.key &&
            item.key.toLowerCase() === ('pricelist1' + tableColumns.priceFilters[filter].value).toLowerCase() &&
            tableColumns.priceFilters[filter].active,
        )
        if (activeColumns) {
          visibleColumns.push(activeColumns)
        }
      }
      if (pricelists.priceList2Enabled) {
        const activeColumns = defaultCols.find(
          (item, index) =>
            item.key &&
            item.key.toLowerCase() === ('pricelist2' + tableColumns.priceFilters[filter].value).toLowerCase() &&
            tableColumns.priceFilters[filter].active,
        )
        if (activeColumns) {
          visibleColumns.push(activeColumns)
        }
      }
    }

    const leftFixedColumn = visibleColumns.filter(
      item =>
        item.name === ArticleColumn.ArticleCode ||
        alignLeftColumns.indexOf(item.name) !== -1 ||
        alignLeftColumns.indexOf(item.key) !== -1,
    )
    const rightFixedColumn = visibleColumns.filter(
      item => alignRightColumns.indexOf(item.name) !== -1 || alignRightColumns.indexOf(item.key) !== -1,
    )

    if (leftFixedColumn.length > 0) {
      //first set article code as first element
      leftFixedColumn.forEach(element => {
        const visibleIndex = visibleColumns.findIndex(
          x => (x.name && element.name === x.name) || (x.key && element.key === x.key),
        )
        if (visibleIndex !== 0) {
          if (
            element.name === ArticleColumn.ArticleCode ||
            leftFixedColumn.filter(item => item.name === ArticleColumn.ArticleCode).length === 0
          ) {
            visibleColumns.splice(0, 0, visibleColumns.splice(visibleIndex, 1)[0])
          }
        }
      })

      //cycle left columns in order to splice array in correct order as index + 1 (as article code in always in first place)
      alignLeftColumns.forEach((leftItem, indexItem) => {
        leftFixedColumn.forEach(element => {
          const visibleIndex = visibleColumns.findIndex(
            x => (x.name && element.name === x.name) || (x.key && element.key === x.key),
          )
          if (visibleIndex !== 0) {
            if (leftItem === element.name) {
              visibleColumns.splice(indexItem + 1, 0, visibleColumns.splice(visibleIndex, 1)[0])
            }
          }
        })
      })
    }

    //add right column as ordered in alignRightColumn array
    if (rightFixedColumn.length > 0) {
      const priceColumnsAdded = visibleColumns.filter(x => x.price)
      let rightColumnsSet = 0
      alignRightColumns.forEach(rightItem => {
        rightFixedColumn.forEach(element => {
          //price columns is always set correctly
          //add element that is not price
          if (!element.price && rightItem === element.name) {
            const visibleIndex = visibleColumns.findIndex(
              x => (x.name && element.name === x.name) || (x.key && element.key === x.key),
            )
            const indexPosition = visibleColumns.length - priceColumnsAdded.length - rightColumnsSet - 1
            if (visibleIndex < indexPosition || visibleIndex > indexPosition) {
              visibleColumns.splice(indexPosition, 0, visibleColumns.splice(visibleIndex, 1)[0])
              rightColumnsSet++
            }
          }
        })
      })
    }

    handleAvailabiltyAndSizeColumnPosition(visibleColumns, rightFixedColumn)

    setColumns(visibleColumns)
  }, [
    alignLeftColumns,
    alignRightColumns,
    defaultCols,
    extraDescriptionLang,
    pricelists.priceList1Enabled,
    pricelists.priceList2Enabled,
    tableColumns,
  ])

  useEffect(() => {
    if (columns && columns.length && !article.results.length && !loading) {
      handleTableChange({ sortField: '', sortOrder: '' }, {})
    }
  }, [columns])

  useEffect(() => {
    if (rowKeysSelected.length === 1 && editTableDisable) {
      fetchAllArticleDetails(
        rowKeysSelected[0],
        formConfigurationValue,
        movementTabVisible && workspaceMovementVisible,
        dispatch,
      )
    }
  }, [rowKeysSelected])

  const renderedColumns = useMemo(() => {
    return generateTableColumnsToRender(
      columns,
      articles,
      language,
      defaultCols,
      freeWidthColumns,
      allFreeWidthColumns,
      editTableDisable,
      pricelists,
      priceList1,
      priceList2,
      isPurchasePriceVisibile,
      alignLeftColumns,
      alignRightColumns,
      newLineColumns,
      negateTableColumns,
      filterByAvailability,
      toggleAvailability,
      isConfigurationFormGruccia,
      true,
      dispatch,
      tableFilters?.size,
    )
  }, [
    columns,
    articles,
    language,
    defaultCols,
    freeWidthColumns,
    allFreeWidthColumns,
    editTableDisable,
    pricelists,
    priceList1,
    priceList2,
    isPurchasePriceVisibile,
    alignLeftColumns,
    alignRightColumns,
    newLineColumns,
    negateTableColumns,
    filterByAvailability,
    toggleAvailability,
    isConfigurationFormGruccia,
    dispatch,
    tableFilters?.size,
  ])

  const finalColumns = useMemo(() => {
    const finalColumns = []
    let leftColumns = 0
    const rightColumns = []
    let i = 0
    for (i = 0; i < renderedColumns.length; i++) {
      let column = renderedColumns[i]
      if (column.key === sorting.sortField) {
        column.className = column.className + ' ant-table-column-sort'
      }
      if (column.fixed === 'left') {
        finalColumns.splice(leftColumns, 0, column)
        leftColumns++
      } else if (column.fixed === 'right') {
        rightColumns.push(column)
      } else {
        finalColumns.push(column)
      }
    }

    for (i = 0; i < rightColumns.length; i++) {
      finalColumns.push(rightColumns[i])
    }

    return finalColumns
  }, [renderedColumns, sorting])

  const dragProps = useMemo(() => {
    return {
      onDragEnd(fromIndex, toIndex) {
        const cols = [...columns]
        if (cols[fromIndex - 1].name === activeDropdown || (toIndex > 0 && cols[toIndex - 1].name === activeDropdown)) {
          dispatch(setActiveDropdown(''))
        }
        if (toIndex === -1) {
          dispatch(removeColumn(cols[fromIndex - 1].name))
          dispatch(populateTableWithArticles())
        }
        const item = cols.splice(fromIndex - 1, 1)[0]
        cols.splice(toIndex - 1, 0, item)
        setColumns(cols)
      },
      nodeSelector:
        'th:not(.ant-table-cell-fix-left):not(.ant-table-cell-fix-right):not(.availability-column):not(.size-availability-column)',
      lineClassName: 'dragging',
    }
  }, [activeDropdown, columns, dispatch])

  const selectRow = useCallback(
    record => {
      if (editTableDisable) {
        const rowKeys = [...rowKeysSelected]
        if (rowKeys.indexOf(record.key) >= 0) {
          rowKeys.pop()
        } else {
          if (rowKeys.length >= 1) {
            rowKeys.pop()
          }
          rowKeys.push(record.key)
        }
        dispatch(
          saveRowKeys({
            rowKeys: rowKeys,
            formValue: formConfigurationValue,
            movementVisibile: movementTabVisible && workspaceMovementVisible,
          }),
        )
        sendMessage({ vaId: record.key, type: 'Config' })
      } else {
        const rowKeys = [...rowKeysSelected]
        if (rowKeys.indexOf(record.key) >= 0) {
          rowKeys.splice(rowKeys.indexOf(record.key), 1)
        } else {
          rowKeys.push(record.key)
        }
        dispatch(
          saveRowKeys({
            rowKeys: rowKeys,
            formValue: formConfigurationValue,
            movementVisibile: movementTabVisible && workspaceMovementVisible,
          }),
        )
        renderSelectionCountHtml(rowKeys.length, false, language)
      }
    },
    [
      dispatch,
      editTableDisable,
      formConfigurationValue,
      language,
      movementTabVisible,
      workspaceMovementVisible,
      rowKeysSelected,
    ],
  )

  const handleSelectRowKeys = useCallback(
    selectedRowKeys => {
      dispatch(
        saveRowKeys({
          rowKeys: selectedRowKeys,
          formValue: formConfigurationValue,
          movementVisibile: movementTabVisible && workspaceMovementVisible,
        }),
      )
      renderSelectionCountHtml(selectedRowKeys.length, false, language)
    },
    [dispatch, movementTabVisible, workspaceMovementVisible, language, formConfigurationValue],
  )

  const filter = useMemo(() => {
    return getSearchedTableFiltersValue(tableColumns, tableFilters, negateTableColumns, priceFilter, allMarketplace)
  }, [tableColumns, tableFilters, negateTableColumns, priceFilter, allMarketplace])
  const empty = useMemo(() => {
    return retrieveActiveEmptyColumns(emptyTableColumns, emptyExtraDescriptions)
  }, [emptyTableColumns, emptyExtraDescriptions])

  const tableLocale = useMemo(() => {
    if (filterSearchValue === MovementsOption) {
      if (filter.length !== 0 || negateTableColumns.length !== 0 || empty.length !== 0) {
        return {
          emptyText: (
            <Empty
              description={renderTranslation(language, TranslationKeys.tableEmptyTextMovement)}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }
      }
    } else if (filterSearchValue === AvailabilitiesOption) {
      if (filter.length !== 0 || negateTableColumns.length !== 0 || empty.length !== 0) {
        return {
          emptyText: (
            <Empty
              description={renderTranslation(language, TranslationKeys.tableEmptyTextAvailabilities)}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }
      }
    } else if (filterSearchValue === ExistanceOption) {
      if (filter.length !== 0 || negateTableColumns.length !== 0 || empty.length !== 0) {
        return {
          emptyText: (
            <Empty
              description={renderTranslation(language, TranslationKeys.tableEmptyTextExistance)}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }
      }
    }

    return {}
  }, [empty.length, filter.length, negateTableColumns.length, filterSearchValue, language])

  const paginationWithCount = useMemo(() => {
    return {
      ...sorting,
      total: count,
      showTotal: (total, range) =>
        formatString(renderTranslation(language, TranslationKeys.tablePagination), range[0], range[1], total),
    }
  }, [sorting, count, language])

  const articleWithImagesAndDescriptions = useMemo(() => {
    return mapArticleImagesAndDescription(articles)
  }, [articles])

  const getColumnWidth = useCallback(
    element => {
      if (element.key === ArticleColumn.Availability) {
        if (columns.find(x => x.name === ArticleColumn.Size)) {
          return SizeWithAvailabilitySpecialColumn
        }
        return SizeSpecialColumn
      } else if (element.isMarketplace) {
        return ArticleDefaultMarketplaceColLength
      }

      return element.width ? element.width : retrieveColumnWidth(element.name)
    },
    [columns],
  )

  const width = useMemo(() => {
    return freeWidthColumns.length === 0
      ? 'max-content'
      : columns.reduce((prev, curr) => {
          return prev + getColumnWidth(curr)
        }, 0)
  }, [columns, freeWidthColumns.length, getColumnWidth])

  const rowKeys = useMemo(() => {
    return fullEditSelection ? articleWithImagesAndDescriptions.map(element => element.key) : rowKeysSelected
  }, [fullEditSelection, articleWithImagesAndDescriptions, rowKeysSelected])

  const updateTableSize = useCallback(
    sizeToSet => {
      const tableWrapper = document.querySelector('#article-table .ant-table-body')
      const addedHeight = tableWrapper && tableWrapper.scrollWidth !== tableWrapper.clientWidth ? 25 : 0
      const minHeight = articles.length ? 0 : emptyTableHeight
      const sizeToUpdate = Math.max(
        minHeight,
        sizeToSet >= articleRowHeight
          ? Math.min(sizeToSet, articles.length * articleRowHeight + 10) + addedHeight
          : articleRowHeight + addedHeight,
      )
      setYSize(sizeToUpdate)
    },
    [articles.length],
  )

  const handler = useCallback(
    mouseDownEvent => {
      scrollElementHeightPositionHandler(mouseDownEvent, ySize, updateTableSize)
    },
    [ySize, updateTableSize],
  )

  const handlerTouch = useCallback(
    touchEvent => {
      scrollElementHeightPositionTouchHandler(touchEvent, ySize, updateTableSize)
    },
    [ySize, updateTableSize],
  )

  const scrollIntoTable = useCallback(
    record => {
      if (editTableDisable) {
        scrollToTableRow(record.key)
      }
    },
    [editTableDisable],
  )

  if (finalColumns.length === 0) return <div></div>

  return (
    //+ 100 height to keep the div with resizable-control-point element under table
    <div style={{ height: ySize + 100 }}>
      <ReactDragColumnView {...dragProps}>
        <Table
          id='article-table'
          className='article-table custom-scroll-body'
          columns={finalColumns}
          dataSource={articleWithImagesAndDescriptions}
          showSorterTooltip={false}
          scroll={{ x: width, y: ySize }}
          loading={loading}
          pagination={paginationWithCount}
          rowSelection={
            editTableDisable
              ? ({
                  selectedRowKeys: rowKeys,
                  columnWidth: '0', // non toccare se no si bugga tutto senza motivo grazie antd
                  hideSelectAll: editTableDisable,
                  renderCell: (checked, record, index, originNode) => null,
                } as TableRowSelection<any>)
              : ({
                  selectedRowKeys: rowKeys,
                  columnWidth: 50,
                  hideSelectAll: editTableDisable,
                  onChange: selectedRowKeys => {
                    const currentSelection = [...rowKeysSelected]

                    for (let i = 0; i < selectedRowKeys.length; i++) {
                      if (currentSelection.indexOf(selectedRowKeys[i]) === -1) {
                        currentSelection.push(selectedRowKeys[i])
                      }
                    }

                    const keysToRemove = articles.map(result => {
                      return selectedRowKeys.indexOf(result.key) === -1 ? result.key : null
                    })

                    const selectionFiltered = currentSelection.filter(element => {
                      return keysToRemove.indexOf(element) === -1
                    })

                    handleSelectRowKeys(selectionFiltered)
                  },
                  selections: [
                    {
                      key: 'FULL_SELECTION',
                      text: renderTranslation(language, TranslationKeys.tableSelectionMenuSelectAll),
                      onSelect: changableRowKeys => {
                        dispatch(setFullEditSelection(true))
                        renderSelectionCountHtml(0, true, language)
                      },
                    },
                    {
                      key: 'CURRENT_PAGE_SELECTION',
                      text: renderTranslation(language, TranslationKeys.tableSelectionMenuSelectCurrentPage),
                      onSelect: changableRowKeys => {
                        const currentSelection = [...new Set([...rowKeysSelected, ...changableRowKeys])]

                        handleSelectRowKeys(currentSelection)
                      },
                    },
                    {
                      key: 'CURRENT_PAGE_INVERT_SELECTION',
                      text: renderTranslation(language, TranslationKeys.tableSelectionMenuInvertSelection),
                      onSelect: changableRowKeys => {
                        const keyAlreadySelected = rowKeysSelected.filter(
                          element => changableRowKeys.indexOf(element) !== -1,
                        )

                        let newSelectedKeys = [...rowKeysSelected]

                        for (let i = 0; i < changableRowKeys.length; i++) {
                          if (newSelectedKeys.indexOf(changableRowKeys[i]) === -1) {
                            newSelectedKeys.push(changableRowKeys[i])
                          }
                        }

                        newSelectedKeys = newSelectedKeys.filter(element => {
                          return keyAlreadySelected.indexOf(element) === -1
                        })

                        handleSelectRowKeys(newSelectedKeys)
                      },
                    },
                  ],
                } as TableRowSelection<any>)
          }
          onChange={handleTableChange}
          onRow={record => ({
            onClick: e => {
              e.stopPropagation()
              selectRow(record)

              setTimeout(() => {
                scrollIntoTable(record)
              }, 800)
            },
          })}
          size='middle'
          tableLayout='fixed'
          locale={tableLocale}
        />
      </ReactDragColumnView>
      <div className='resizable-control-point' onMouseDown={handler} onTouchStart={handlerTouch}>
        <ManigliaIcon />
      </div>
    </div>
  )
}
export default Articles
